"use client"

import type React from "react"
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  Users,
  FileText,
  CreditCard,
  AlertCircle,
  Package,
  Truck,
  BarChart,
  Map,
  UserCheck,
  MessageSquare,
  CheckSquare,
  LogOut,
  Search,
  Clipboard,
  Banknote,
  Receipt,
  Network,
  DollarSign,
  TrendingUp,
  X,
  ChevronDown,
  PieChart,
  Activity,
  Settings,
  Layers,
  Briefcase,
  Wallet,
  Wrench,
  MapPin,
  Store,
  Shield,
} from "lucide-react"
import { removeToken } from "../utils/auth.ts"
import axiosInstance from "../utils/axiosConfig.ts"
import { LOGIN_ROUTE } from "../utils/authRedirects.ts"
import { smartoltService } from "../services/smartolt.service.ts"
import { useSmartOLTAvailability } from "../pages/smartolt/hooks/useSmartOLTAvailability.ts"
import { buildSmartOltMenuGroup, type SmartOltSidebarOlt } from "../pages/smartolt/smartoltNav.ts"
import {
  collectNestedGroupIds,
  flattenLeaves,
  groupIsActive,
  isGroup,
  isNestedGroup,
  isPathActive,
  itemIsActive,
  leafIsActive,
  nestedGroupIsActive,
  type SidebarGroup,
  type SidebarItem,
  type SidebarLeaf,
  type SidebarLink,
  type SidebarNavChild,
  type SidebarNestedGroup,
} from "../utils/sidebarNav.ts"
import { useCompany } from "../context/CompanyContext.tsx"
import { canManageVendors } from "../utils/companyAccess.ts"

const SIDEBAR_OPEN_DROPDOWNS_KEY = "sidebar_open_dropdowns"
const SIDEBAR_SCROLL_POSITION_KEY = "sidebar_scroll_position"

export type {
  SidebarGroup,
  SidebarItem,
  SidebarLeaf,
  SidebarLink,
  SidebarNavChild,
  SidebarNestedGroup,
}

export const pinnedItems: SidebarLeaf[] = [
  { title: "Executive Overview", path: "/reporting/executive", icon: PieChart },
  { title: "Customers", path: "/customer-management", icon: Users },
  { title: "Billing & Invoices", path: "/billing-invoices", icon: Receipt },
  { title: "Payments", path: "/payment-management", icon: CreditCard },
  { title: "Complaints", path: "/complaint-management", icon: AlertCircle },
]

export const menuItems: SidebarItem[] = [
  {
    id: "reporting",
    title: "Reporting",
    icon: BarChart,
    isDropdown: true,
    subItems: [
      { title: "Executive Overview", path: "/reporting/executive", icon: PieChart },
      { title: "Customer Analytics", path: "/reporting/customers", icon: Users },
      { title: "Financial Analytics", path: "/reporting/financial", icon: DollarSign },
      { title: "Service & Support", path: "/reporting/service", icon: Wrench },
      { title: "Inventory Analytics", path: "/reporting/inventory", icon: Package },
      { title: "Employee Performance", path: "/reporting/employees", icon: UserCheck },
      { title: "Regional Analysis", path: "/reporting/regional", icon: MapPin },
      { title: "Service Plans", path: "/reporting/plans", icon: FileText },
      { title: "Collections", path: "/reporting/collections", icon: Wallet },
      { title: "Operations", path: "/reporting/operations", icon: Activity },
    ],
  },
  {
    id: "people",
    title: "People & Vendors",
    icon: Briefcase,
    isDropdown: true,
    subItems: [
      { title: "Employee Management", path: "/employee-management", icon: Users },
      { title: "Vendor Management", path: "/vendor-management", icon: Store },
      { title: "Supplier Management", path: "/supplier-management", icon: Truck },
      { title: "ISP Management", path: "/isp-management", icon: Network },
    ],
  },
  {
    id: "customers",
    title: "Customers",
    icon: Users,
    isDropdown: true,
    subItems: [
      { title: "Customer Management", path: "/customer-management", icon: Users },
      { title: "Service Plan Management", path: "/service-plan-management", icon: FileText },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    icon: DollarSign,
    isDropdown: true,
    subItems: [
      { title: "Payment Management", path: "/payment-management", icon: CreditCard },
      { title: "ISP Payments", path: "/isp-payment-management", icon: Network },
      { title: "Billing & Invoices", path: "/billing-invoices", icon: Receipt },
      { title: "Bank Accounts", path: "/bank-management", icon: Banknote },
      { title: "Expense Management", path: "/expense-management", icon: DollarSign },
      { title: "Extra Income", path: "/extra-income-management", icon: TrendingUp },
    ],
  },
  {
    id: "ops",
    title: "Ops",
    icon: AlertCircle,
    isDropdown: true,
    subItems: [
      { title: "Complaint Management", path: "/complaint-management", icon: AlertCircle },
      { title: "Task Management", path: "/task-management", icon: CheckSquare },
      { title: "Recovery Tasks", path: "/recovery-task-management", icon: UserCheck },
    ],
  },
  {
    id: "inventory",
    title: "Inventory",
    icon: Package,
    path: "/inventory-management",
  },
  {
    id: "areas",
    title: "Areas",
    icon: Map,
    isDropdown: true,
    subItems: [
      { title: "Area/Zone Management", path: "/area-zone-management", icon: Map },
      { title: "Sub-Zones", path: "/areas", icon: Layers },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    icon: Shield,
    isDropdown: true,
    subItems: [
      { title: "Messaging", path: "/message-management", icon: MessageSquare },
      { title: "Communication Queue", path: "/whatsapp/queue", icon: MessageSquare },
      { title: "Bulk Sender", path: "/whatsapp/bulk-sender", icon: MessageSquare },
      { title: "WhatsApp & Email Settings", path: "/whatsapp/settings", icon: Settings },
      { title: "Logs Management", path: "/logs-management", icon: Clipboard },
    ],
  },
  buildSmartOltMenuGroup(),
]

function readOpenDropdowns(): string[] {
  try {
    const saved = localStorage.getItem(SIDEBAR_OPEN_DROPDOWNS_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

function readScrollPosition(): number {
  try {
    const saved = localStorage.getItem(SIDEBAR_SCROLL_POSITION_KEY)
    return saved ? parseInt(saved, 10) || 0 : 0
  } catch {
    return 0
  }
}

function activeGroupId(pathname: string, items: SidebarItem[] = menuItems): string | null {
  const activeGroup = items.find((item) => isGroup(item) && groupIsActive(pathname, item))
  return activeGroup?.id ?? null
}

function childMatchesQuery(item: SidebarNavChild, q: string): boolean {
  if (item.title.toLowerCase().includes(q)) return true
  if (isNestedGroup(item)) {
    return item.subItems.some((leaf) => leaf.title.toLowerCase().includes(q))
  }
  return false
}

function collectSearchLeaves(query: string, items: SidebarItem[] = menuItems): SidebarLeaf[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const seen = new Set<string>()
  const results: SidebarLeaf[] = []

  const push = (leaf: SidebarLeaf) => {
    if (seen.has(leaf.path)) return
    seen.add(leaf.path)
    results.push(leaf)
  }

  for (const pin of pinnedItems) {
    if (pin.title.toLowerCase().includes(q)) push(pin)
  }
  for (const item of items) {
    if (!isGroup(item)) {
      if (item.title.toLowerCase().includes(q)) {
        push({ title: item.title, path: item.path, icon: item.icon })
      }
      continue
    }
    if (item.title.toLowerCase().includes(q)) {
      flattenLeaves(item.subItems).forEach(push)
      continue
    }
    item.subItems.forEach((sub) => {
      if (isNestedGroup(sub)) {
        if (sub.title.toLowerCase().includes(q)) {
          sub.subItems.forEach(push)
          return
        }
        sub.subItems.forEach((leaf) => {
          if (leaf.title.toLowerCase().includes(q)) push(leaf)
        })
        return
      }
      if (sub.title.toLowerCase().includes(q)) push(sub)
    })
  }
  return results
}

interface SidebarProps {
  isOpen: boolean
  toggleSidebar: () => void
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const location = useLocation()
  const navRef = useRef<HTMLElement>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)
  const flyoutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(false)
  const [flyoutId, setFlyoutId] = useState<string | null>(null)
  const [flyoutTop, setFlyoutTop] = useState(56)
  const [openDropdowns, setOpenDropdowns] = useState<string[]>(readOpenDropdowns)
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null)
  const [mobileOpenNestedDropdown, setMobileOpenNestedDropdown] = useState<string | null>(null)
  const persistedOpenRef = useRef<string[]>(readOpenDropdowns())
  const scrollRestoredRef = useRef(false)
  const [smartOltOlts, setSmartOltOlts] = useState<SmartOltSidebarOlt[]>([])
  const { available: smartOltAvailable } = useSmartOLTAvailability()
  const { company } = useCompany()
  const showVendorManagement = canManageVendors(company)

  const resolvedMenuItems = useMemo(
    () =>
      menuItems
        .filter((item) => item.id !== "smartolt" || smartOltAvailable)
        .map((item) => {
          if (item.id === "smartolt") return buildSmartOltMenuGroup(smartOltOlts)
          if (item.id === "people" && isGroup(item) && !showVendorManagement) {
            return {
              ...item,
              title: "People",
              subItems: item.subItems.filter(
                (sub) => !isNestedGroup(sub) && sub.path !== "/vendor-management",
              ),
            }
          }
          return item
        }),
    [smartOltAvailable, smartOltOlts, showVendorManagement],
  )

  useEffect(() => {
    if (!smartOltAvailable) {
      setSmartOltOlts([])
      return
    }
    let cancelled = false
    const loadOlts = smartoltService.getOlts
    void Promise.resolve(typeof loadOlts === "function" ? loadOlts() : [])
      .then((rows) => {
        if (cancelled) return
        setSmartOltOlts(
          (Array.isArray(rows) ? rows : []).map((row) => ({
            olt_id: String(row.olt_id),
            name: row.name || `OLT ${row.olt_id}`,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setSmartOltOlts([])
      })
    return () => {
      cancelled = true
    }
  }, [smartOltAvailable])

  const expanded = isOpen
  const mobileDrawerOpen = isMobile && isOpen

  const clearFlyoutCloseTimer = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current)
      flyoutCloseTimer.current = null
    }
  }

  /** Align flyout top with the hovered/clicked icon; clamp so it stays on-screen. */
  const positionFlyout = (anchor: HTMLElement | null) => {
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const estimatedH = flyoutRef.current?.offsetHeight ?? 160
    const maxTop = Math.max(56, window.innerHeight - estimatedH - 8)
    setFlyoutTop(Math.min(Math.max(rect.top, 56), maxTop))
  }

  const openFlyout = (id: string, anchor?: HTMLElement | null) => {
    clearFlyoutCloseTimer()
    if (anchor) positionFlyout(anchor)
    setFlyoutId(id)
  }

  const scheduleFlyoutClose = () => {
    clearFlyoutCloseTimer()
    flyoutCloseTimer.current = setTimeout(() => setFlyoutId(null), 180)
  }

  useEffect(() => () => clearFlyoutCloseTimer(), [])

  // Re-clamp after flyout paints (real height known)
  useEffect(() => {
    if (!flyoutId || !flyoutRef.current) return
    const el = flyoutRef.current
    const h = el.offsetHeight
    const maxTop = Math.max(56, window.innerHeight - h - 8)
    const clamped = Math.min(Math.max(flyoutTop, 56), maxTop)
    if (clamped !== flyoutTop) setFlyoutTop(clamped)
  }, [flyoutId, flyoutTop])

  useEffect(() => {
    if (isMobile) return
    try {
      localStorage.setItem(SIDEBAR_OPEN_DROPDOWNS_KEY, JSON.stringify(openDropdowns))
      if (!searchQuery.trim()) persistedOpenRef.current = openDropdowns
    } catch {
      // ignore
    }
  }, [openDropdowns, searchQuery, isMobile])

  const saveScrollPosition = useCallback(() => {
    if (!navRef.current) return
    const position = navRef.current.scrollTop
    try {
      localStorage.setItem(SIDEBAR_SCROLL_POSITION_KEY, String(position))
    } catch {
      // ignore
    }
  }, [])

  const restoreScrollPosition = useCallback(() => {
    if (!navRef.current) return
    navRef.current.scrollTop = readScrollPosition()
  }, [])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Lock page scroll while mobile drawer is open
  useEffect(() => {
    if (!mobileDrawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileDrawerOpen])

  // Mobile drawer: one section open, ignore desktop persisted state
  useEffect(() => {
    if (!mobileDrawerOpen) return
    setMobileOpenDropdown(activeGroupId(location.pathname, resolvedMenuItems))
    setMobileOpenNestedDropdown(null)
    setSearchQuery("")
  }, [mobileDrawerOpen, location.pathname, resolvedMenuItems])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isOpen && setIsOpen) {
        const sidebar = document.getElementById("sidebar")
        if (sidebar && !sidebar.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }
      if (flyoutId && flyoutRef.current && !flyoutRef.current.contains(event.target as Node)) {
        const sidebar = document.getElementById("sidebar")
        if (sidebar && sidebar.contains(event.target as Node)) return
        setFlyoutId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobile, isOpen, setIsOpen, flyoutId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      setFlyoutId(null)
      if (isMobile && isOpen && setIsOpen) setIsOpen(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isMobile, isOpen, setIsOpen])

  useEffect(() => {
    setFlyoutId(null)
  }, [location.pathname])

  // Auto-expand group for current route (desktop only)
  useEffect(() => {
    if (isMobile) return
    const id = activeGroupId(location.pathname, resolvedMenuItems)
    const nestedIds = collectNestedGroupIds(location.pathname, resolvedMenuItems)
    if (!id && nestedIds.length === 0) return
    setOpenDropdowns((prev) => {
      const next = new Set(prev)
      if (id) next.add(id)
      nestedIds.forEach((nestedId) => next.add(nestedId))
      return [...next]
    })
  }, [location.pathname, isMobile, resolvedMenuItems])

  // Restore scroll when expanded / mounted (desktop) — do NOT include openDropdowns
  // in deps; toggling a dropdown must not reset the scroll position.
  useEffect(() => {
    if (isMobile || !expanded) return
    if (scrollRestoredRef.current) return // already restored; don't jump on dropdown toggle
    const id = requestAnimationFrame(() => {
      restoreScrollPosition()
      scrollRestoredRef.current = true
      const active = navRef.current?.querySelector("[data-sidebar-active='true']")
      if (active && typeof (active as HTMLElement).scrollIntoView === "function") {
        ;(active as HTMLElement).scrollIntoView({ block: "nearest" })
      }
    })
    return () => cancelAnimationFrame(id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, isMobile, restoreScrollPosition])

  const q = searchQuery.trim().toLowerCase()
  const mobileSearchResults = useMemo(
    () => (isMobile && q ? collectSearchLeaves(q, resolvedMenuItems) : []),
    [isMobile, q, resolvedMenuItems],
  )

  // Search: auto-open matching groups (desktop only)
  useEffect(() => {
    if (isMobile) return
    if (!q) {
      setOpenDropdowns(persistedOpenRef.current)
      return
    }
    const matchIds = resolvedMenuItems
      .filter((item) => {
        if (!isGroup(item)) {
          return item.title.toLowerCase().includes(q)
        }
        if (item.title.toLowerCase().includes(q)) return true
        return item.subItems.some((s) => childMatchesQuery(s, q))
      })
      .flatMap((item) => {
        if (!isGroup(item)) return [item.id]
        const nested = item.subItems
          .filter((s) => isNestedGroup(s) && childMatchesQuery(s, q))
          .map((s) => (s as SidebarNestedGroup).id)
        return [item.id, ...nested]
      })
    setOpenDropdowns(matchIds)
  }, [q, isMobile, resolvedMenuItems])

  const filteredMenuItems = useMemo(() => {
    if (!q) return resolvedMenuItems
    return resolvedMenuItems.filter((item) => {
      if (!isGroup(item)) return item.title.toLowerCase().includes(q)
      if (item.title.toLowerCase().includes(q)) return true
      return item.subItems.some((s) => childMatchesQuery(s, q))
    })
  }, [q, resolvedMenuItems])

  const filteredPinned = useMemo(() => {
    if (!q) return pinnedItems
    return pinnedItems.filter((p) => p.title.toLowerCase().includes(q))
  }, [q])

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/auth/logout")
      removeToken()
      navigate(LOGIN_ROUTE)
    } catch (error) {
      console.error("Logout failed", error)
    }
  }

  const toggleDropdown = (id: string) => {
    if (isMobile) {
      setMobileOpenDropdown((prev) => (prev === id ? null : id))
      // Reset nested when switching parent
      setMobileOpenNestedDropdown(null)
      return
    }
    setOpenDropdowns((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    )
  }

  const toggleNestedDropdown = (id: string) => {
    if (isMobile) {
      setMobileOpenNestedDropdown((prev) => (prev === id ? null : id))
      return
    }
    setOpenDropdowns((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    )
  }

  const isDropdownOpen = (id: string) =>
    isMobile ? mobileOpenDropdown === id : openDropdowns.includes(id)

  const isNestedDropdownOpen = (id: string) =>
    isMobile ? mobileOpenNestedDropdown === id : openDropdowns.includes(id)

  const handleLinkClick = () => {
    saveScrollPosition()
    setFlyoutId(null)
    if (isMobile && setIsOpen) setIsOpen(false)
  }

  const flyoutGroup = useMemo(() => {
    if (!flyoutId) return null
    const item = resolvedMenuItems.find((m) => m.id === flyoutId)
    return item && isGroup(item) ? item : null
  }, [flyoutId, resolvedMenuItems])

  const renderLeafLink = (sub: SidebarLeaf, opts?: { inFlyout?: boolean; compact?: boolean }) => {
    const SubIcon = sub.icon
    const active = leafIsActive(location.pathname, sub)
    const mobileRow = isMobile && !opts?.inFlyout
    return (
      <Link
        key={sub.path}
        to={sub.path}
        data-sidebar-active={active ? "true" : undefined}
        className={`group flex items-center rounded-lg transition-colors duration-150 ${
          mobileRow ? "h-10 px-3 text-sm" : "px-2.5 py-1.5 rounded-md text-sm"
        } ${
          active
            ? "bg-blue-50 text-blue-700"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
        } ${opts?.inFlyout ? "w-full" : ""}`}
        onClick={handleLinkClick}
      >
        <SubIcon
          className={`mr-2.5 flex-shrink-0 ${
            mobileRow ? "h-[18px] w-[18px]" : "h-4 w-4"
          } ${
            active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"
          }`}
        />
        <span className={`truncate font-medium ${active ? "text-blue-700" : ""}`}>{sub.title}</span>
      </Link>
    )
  }

  const renderNestedGroup = (group: SidebarNestedGroup) => {
    const NestedIcon = group.icon
    const nestedOpen = isNestedDropdownOpen(group.id)
    const nestedActive = nestedGroupIsActive(location.pathname, group)
    const nestedChildren = group.subItems.filter(
      (leaf) => !q || group.title.toLowerCase().includes(q) || leaf.title.toLowerCase().includes(q),
    )

    return (
      <div key={group.id}>
        <button
          type="button"
          className={`group flex w-full items-center rounded-md px-2.5 py-1.5 text-sm transition-colors ${
            nestedActive ? "bg-slate-50 text-slate-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
          }`}
          onClick={(e) => {
            e.stopPropagation()
            toggleNestedDropdown(group.id)
          }}
        >
          <NestedIcon
            className={`mr-2.5 h-4 w-4 flex-shrink-0 ${nestedActive ? "text-blue-600" : "text-slate-400"}`}
          />
          <span className="min-w-0 flex-1 truncate text-left font-medium">{group.title}</span>
          <ChevronDown
            className={`ml-2 h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform ${
              nestedOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {nestedOpen && nestedChildren.length > 0 && (
          <div className="ml-3 space-y-0.5 border-l border-slate-200 pl-2">
            {nestedChildren.map((leaf) => renderLeafLink(leaf))}
          </div>
        )}
      </div>
    )
  }

  const onCollapsedIconClick = (item: SidebarItem, anchor: HTMLElement) => {
    if (!isGroup(item)) {
      navigate(item.path)
      handleLinkClick()
      return
    }
    if (item.subItems.length === 1) {
      navigate(item.subItems[0].path)
      handleLinkClick()
      return
    }
    if (flyoutId === item.id) {
      setFlyoutId(null)
      return
    }
    positionFlyout(anchor)
    setFlyoutId(item.id)
  }

  const onCollapsedIconEnter = (item: SidebarItem, anchor: HTMLElement) => {
    if (!isGroup(item) || item.subItems.length <= 1) return
    openFlyout(item.id, anchor)
  }

  return (
    <>
      {mobileDrawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-hidden
          onClick={() => setIsOpen && setIsOpen(false)}
        />
      )}

      <aside
        id="sidebar"
        className={`
          flex flex-col overflow-hidden border-r border-slate-200 bg-white
          ${
            isMobile
              ? `fixed left-0 top-0 z-50 h-dvh w-[min(100vw,20rem)] shadow-xl transition-transform duration-300 ease-out ${
                  isOpen ? "translate-x-0" : "-translate-x-full pointer-events-none"
                }`
              : `fixed top-14 z-30 h-[calc(100vh-3.5rem)] transition-[width] duration-300 ease-in-out ${
                  expanded ? "w-72" : "w-20"
                } left-0`
          }
        `}
      >
        {mobileDrawerOpen && (
          <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-deep-ocean">Menu</p>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              onClick={() => setIsOpen && setIsOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {expanded && (
          <div className={`flex-shrink-0 border-b border-slate-100 ${isMobile ? "px-4 py-3" : "p-3"}`}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search modules..."
                className={`w-full rounded-lg border border-slate-200 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isMobile ? "h-10 px-3 pr-9 text-base" : "h-9 px-3 pr-9 text-sm"
                }`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
            </div>
          </div>
        )}

        <nav
          ref={navRef}
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 ${
            expanded ? (isMobile ? "px-3" : "scrollbar-thin px-2") : "flex flex-col items-center gap-0.5 px-2"
          }`}
          onScroll={isMobile ? undefined : saveScrollPosition}
        >
          {isMobile && q ? (
            <div className="space-y-1">
              {mobileSearchResults.length > 0 ? (
                mobileSearchResults.map((leaf) => renderLeafLink(leaf))
              ) : (
                <p className="px-3 py-6 text-center text-sm text-slate-500">No modules match your search.</p>
              )}
            </div>
          ) : (
            <>
              {/* Pinned — desktop expanded only */}
              {!isMobile && filteredPinned.length > 0 && (
                <div className={`mb-2 ${expanded ? "" : "flex w-full flex-col items-center gap-0.5"}`}>
                  {expanded && (
                    <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      Frequent
                    </p>
                  )}
                  {filteredPinned.map((pin) => {
                    const Icon = pin.icon
                    const active = isPathActive(location.pathname, pin.path)
                    if (!expanded) {
                      return (
                        <Link
                          key={pin.path}
                          to={pin.path}
                          title={pin.title}
                          data-sidebar-active={active ? "true" : undefined}
                          onClick={handleLinkClick}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                            active
                              ? "bg-blue-600 text-white"
                              : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          }`}
                        >
                          <Icon className="h-[18px] w-[18px]" />
                          <span className="sr-only">{pin.title}</span>
                        </Link>
                      )
                    }
                    return renderLeafLink(pin)
                  })}
                  {expanded && <div className="my-2 border-t border-slate-100" />}
                </div>
              )}

              {filteredMenuItems.map((item) => {
                const Icon = item.icon
                const containsActive = itemIsActive(location.pathname, item)
                const dropdownOpen = isGroup(item) && isDropdownOpen(item.id)

                if (!expanded) {
                  return (
                    <div
                      key={item.id}
                      className="relative flex w-full justify-center"
                      onMouseEnter={(e) => onCollapsedIconEnter(item, e.currentTarget)}
                      onMouseLeave={scheduleFlyoutClose}
                    >
                      <button
                        type="button"
                        title={item.title}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                          containsActive
                            ? "bg-slate-100 text-blue-700 ring-1 ring-blue-200"
                            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        }`}
                        onClick={(e) => onCollapsedIconClick(item, e.currentTarget)}
                      >
                        <Icon className="h-[18px] w-[18px]" />
                        <span className="sr-only">{item.title}</span>
                      </button>
                    </div>
                  )
                }

                if (!isGroup(item)) {
                  const active = isPathActive(location.pathname, item.path)
                  return (
                    <div key={item.id} className="mb-0.5">
                      <Link
                        to={item.path}
                        data-sidebar-active={active ? "true" : undefined}
                        onClick={handleLinkClick}
                        className={`group flex w-full items-center rounded-lg transition-colors ${
                          isMobile ? "h-11 px-3 text-sm" : "h-9 px-3 text-sm"
                        } ${
                          active
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                        }`}
                      >
                        <Icon
                          className={`mr-3 h-[18px] w-[18px] flex-shrink-0 ${
                            active ? "text-blue-600" : "text-slate-500"
                          }`}
                        />
                        <span className="truncate font-medium">{item.title}</span>
                      </Link>
                    </div>
                  )
                }

                const children = item.subItems.filter(
                  (sub) => !q || item.title.toLowerCase().includes(q) || childMatchesQuery(sub, q),
                )

                return (
                  <div key={item.id} className="mb-0.5">
                    <button
                      type="button"
                      className={`group relative flex w-full items-center rounded-lg transition-colors ${
                        isMobile ? "h-11 px-3 text-sm" : "h-9 px-3 text-sm"
                      } ${
                        containsActive
                          ? "bg-slate-100 text-slate-800"
                          : dropdownOpen
                            ? "bg-slate-50 text-slate-700"
                            : "text-slate-600 hover:bg-slate-100"
                      }`}
                      onClick={() => toggleDropdown(item.id)}
                    >
                      {containsActive && (
                        <span className="absolute bottom-1.5 left-0 top-1.5 w-0.5 rounded-full bg-blue-600" />
                      )}
                      <Icon className="mr-3 h-[18px] w-[18px] flex-shrink-0 text-slate-500" />
                      <span className="min-w-0 flex-1 truncate text-left font-medium">{item.title}</span>
                      <ChevronDown
                        className={`ml-2 h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${
                          dropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {dropdownOpen && children.length > 0 && (
                      <div
                        className={`mt-0.5 space-y-0.5 ${
                          isMobile ? "ml-1 border-l border-slate-200 pl-2" : "ml-2 border-l border-slate-200 pl-2"
                        }`}
                      >
                        {children.map((sub) =>
                          isNestedGroup(sub) ? renderNestedGroup(sub) : renderLeafLink(sub),
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </nav>

        <div
          className={`flex-shrink-0 border-t border-slate-200 bg-white ${
            isMobile ? "p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" : expanded ? "p-3" : "flex justify-center p-2"
          }`}
        >
          <button
            type="button"
            className={`group flex items-center rounded-lg text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 ${
              expanded ? (isMobile ? "h-11 w-full px-3" : "h-9 w-full px-3") : "h-9 w-9 justify-center"
            }`}
            onClick={handleLogout}
            title={!expanded ? "Logout" : undefined}
          >
            <LogOut className={`h-[18px] w-[18px] flex-shrink-0 ${expanded ? "mr-2.5" : ""}`} />
            {expanded ? <span className="text-sm font-medium">Logout</span> : <span className="sr-only">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Collapsed flyout — aligned to the hovered/clicked icon */}
      {!expanded && !isMobile && flyoutGroup && (
        <div
          ref={flyoutRef}
          style={{ top: flyoutTop, left: 80 }}
          className="fixed z-40 w-64 max-h-[min(24rem,calc(100vh-1rem))] overflow-y-auto bg-white border border-slate-200 rounded-r-lg shadow-lg py-2"
          onMouseEnter={clearFlyoutCloseTimer}
          onMouseLeave={scheduleFlyoutClose}
        >
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {flyoutGroup.title}
          </p>
          <div className="px-1.5 space-y-0.5">
            {flyoutGroup.subItems.map((sub) => {
              if (!isNestedGroup(sub)) {
                return renderLeafLink(sub, { inFlyout: true })
              }
              return (
                <div key={sub.id}>
                  <p className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {sub.title}
                  </p>
                  {sub.subItems.map((leaf) => renderLeafLink(leaf, { inFlyout: true }))}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <style>{`
        .scrollbar-thin::-webkit-scrollbar { width: 6px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 3px; }
        .scrollbar-thin:hover::-webkit-scrollbar-thumb { background: #cbd5e1; }
      `}</style>
    </>
  )
}
